import { Core } from "@faire/design-tokens";
import trackClick from "@faire/web-api/api/click-track/post";
import { trackCategoriesC2ToC3NavigationC2CategoryCarouselClick } from "@faire/web-api/events/categories/click/c2ToC3NavigationC2CategoryCarousel";
import { trackCategoriesViewC2ToC3NavigationC2CategoryCarouselView } from "@faire/web-api/events/categories/view/viewC2ToC3NavigationC2CategoryCarousel";
import { ICategoryTile } from "@faire/web-api/indigofair/data/ICategoryTile";
import { IClickInfo } from "@faire/web-api/indigofair/data/IClickInfo";
import { IImage } from "@faire/web-api/indigofair/data/IImage";
import { ISimpleImageTitle } from "@faire/web-api/indigofair/data/ISimpleImageTitle";
import { optimizeImageUrlFillSquare } from "@faire/web/common/images/optimizeImages";
import { useIsSSR } from "@faire/web/common/server-side-rendering/isSSR";
import { Flex, HorizontalLine } from "@faire/web/slate/Layout";
import { getSpacing } from "@faire/web/slate/spacing";
import { responsiveMediaQueries } from "@faire/web/slate/Theme/responsiveMediaQueries";
import { Typography } from "@faire/web/slate/Typography";
import { useResponsiveValue } from "@faire/web/ui/hooks/useResponsiveValue";
import { Link } from "@faire/web/ui/LinkAnchor";
import React, { useCallback, useEffect, useMemo, useRef } from "react";
import styled, { css } from "styled-components";

import {
  CategoryTileAndImageTitleTileType,
  isCategoryTile,
  isSimpleImageTile,
} from "@faire/retailer/components/Carousel/CategoryTileAndImageTitleTileType";
import {
  filterLongCategoryName,
  isLongCategoryName,
} from "@faire/retailer/components/Carousel/categoryTileUtils";
import { FiniteScrollCarousel } from "@faire/retailer/components/Carousel/FiniteScrollCarousel/FiniteScrollCarousel";
import { AspectRatioPicture } from "@faire/retailer/components/Image/AspectRatioPicture";
import { EAEAEA_PIXEL } from "@faire/retailer/consts/EAEAEA_PIXEL";
import { MARKETPLACE_LEFT_AND_RIGHT_MARGIN_MOBILE } from "@faire/retailer/features/Marketplace/styleConsts";
import { categoryPath } from "@faire/retailer/paths/category/categoryPath";
import { useCategoryStore } from "@faire/retailer/stores/domain/CategoryStore";
import { findClosestActiveCategoryUrlIdsByPathIdentifier } from "@faire/retailer/stores/domain/CategoryStore/findByPathIdentifier";

const IMAGE_SIZE = 72;
const ITEM_GAP = 16;
const DESKTOP_ITEM_WIDTH = 88;
const MOBILE_ITEM_WIDTH = 96;
// we won't display anything if categoryTiles below this threshold
const CAROUSEL_THRESHOLD = 2;
const CAROUSEL_TYPE = "circular";

interface IProps {
  tiles: CategoryTileAndImageTitleTileType[];
  eventTrackingPrefix: string;
  categoryPathIdentifier: string;
  className?: string;
  requestId?: string;
}

const getImageUrl = (isSSR: boolean, image?: IImage) =>
  image ? optimizeImageUrlFillSquare(image, isSSR, IMAGE_SIZE) : EAEAEA_PIXEL;

export const CircularCategoryCarousel: React.FC<IProps> = (props) => {
  const isThresholdReached = useMemo(() => {
    return props.tiles.length >= CAROUSEL_THRESHOLD;
  }, [props.tiles.length]);
  const didMount = useRef(false);
  const { categories } = useCategoryStore();

  useEffect(() => {
    if (didMount.current) {
      return;
    }
    didMount.current = true;
    if (isThresholdReached) {
      trackCategoriesViewC2ToC3NavigationC2CategoryCarouselView(
        props.categoryPathIdentifier,
        CAROUSEL_TYPE
      );
    }
  }, [isThresholdReached, props.categoryPathIdentifier]);

  const onDragStart = useCallback(
    (e: React.MouseEvent) => e.preventDefault(),
    []
  );

  const eventContainerName = useMemo(() => {
    return `c2-to-c3-circular-carousel-${props.categoryPathIdentifier}`;
  }, [props.categoryPathIdentifier]);

  const trackClickEvent = useCallback(
    (token?: string) => {
      trackClick(
        IClickInfo.build({
          container_name: eventContainerName,
          token: token,
          type: IClickInfo.Type.CATEGORY,
          request_id: props.requestId,
        })
      );
    },
    [eventContainerName, props.requestId]
  );

  const onTileClick = useCallback(
    (trackingToken?: string) => () => {
      trackCategoriesC2ToC3NavigationC2CategoryCarouselClick(
        props.categoryPathIdentifier,
        CAROUSEL_TYPE,
        trackingToken ?? ""
      );
      trackClickEvent(trackingToken);
    },
    [props.categoryPathIdentifier, trackClickEvent]
  );

  const isSSR = useIsSSR();

  const renderCategoryTile = useCallback(
    (categoryTile: ICategoryTile, index: number) => {
      if (categoryTile.category_path_identifier === undefined) {
        return null;
      }
      const categoryUrlIds = findClosestActiveCategoryUrlIdsByPathIdentifier(
        categoryTile.category_path_identifier,
        categories
      );
      if (categoryUrlIds === undefined) {
        return null;
      }
      const imageURL = getImageUrl(isSSR, categoryTile.image);
      return (
        <LinkWrapper
          to={categoryPath({ urlIds: categoryUrlIds })}
          key={`category-tile-${index}`}
          onDragStart={onDragStart}
          onClick={onTileClick(categoryTile.category_path_identifier)}
          data-test-id="circular-category-tile"
        >
          <CircularImageWrapper $width={IMAGE_SIZE} $ratio={1} $hideBorder>
            <img src={imageURL} />
          </CircularImageWrapper>
          <CategoryName
            $isLongCategoryName={isLongCategoryName(categoryTile.name)}
          >
            <AnimatedUnderlineText>
              {filterLongCategoryName(categoryTile.name)}
            </AnimatedUnderlineText>
          </CategoryName>
        </LinkWrapper>
      );
    },
    [categories, isSSR, onTileClick, onDragStart]
  );

  const renderSimpleImageTitle = useCallback(
    (simpleImageTile: ISimpleImageTitle, index: number) => {
      return (
        <LinkWrapper
          to={simpleImageTile.cta_link ?? ""}
          key={`simple-image-title-${index}`}
          onDragStart={onDragStart}
          data-test-id="circular-title-tile"
          onClick={onTileClick(simpleImageTile.event_tracking_token)}
        >
          <InnerLinkWrapper>
            <CircularImageWrapper $width={IMAGE_SIZE} $ratio={1} $hideBorder>
              <img src={getImageUrl(isSSR, simpleImageTile.image)} />
            </CircularImageWrapper>
            <TextWrapper>
              <Typography color={Core.text.inverse} align="center">
                {simpleImageTile.main_text}
              </Typography>
            </TextWrapper>
            <CategoryName
              $isLongCategoryName={isLongCategoryName(
                simpleImageTile.description_text
              )}
            >
              <AnimatedUnderlineText>
                {filterLongCategoryName(simpleImageTile.description_text)}
              </AnimatedUnderlineText>
            </CategoryName>
          </InnerLinkWrapper>
        </LinkWrapper>
      );
    },
    [isSSR, onDragStart, onTileClick]
  );

  const renderTiles = useCallback(
    (tile: CategoryTileAndImageTitleTileType, index: number) => {
      if (isCategoryTile(tile)) {
        return renderCategoryTile(tile, index);
      }
      if (isSimpleImageTile(tile)) {
        return renderSimpleImageTitle(tile, index);
      }
      return null;
    },
    [renderCategoryTile, renderSimpleImageTitle]
  );

  const expectedItemsPerSlide = useResponsiveValue({
    mobileAndAbove: 3,
    xLargeAndAbove: 5,
  });

  const isMoreThanOneSlide = useMemo(() => {
    return props.tiles.length > expectedItemsPerSlide;
  }, [expectedItemsPerSlide, props.tiles.length]);

  const itemsPerSlide = useMemo(() => {
    if (isMoreThanOneSlide) {
      return expectedItemsPerSlide;
    }
    return props.tiles.length;
  }, [expectedItemsPerSlide, isMoreThanOneSlide, props.tiles.length]);

  if (!isThresholdReached) {
    return null;
  }

  return (
    <>
      <StyledFiniteScrollCarousel
        itemsPerSlide={itemsPerSlide}
        items={props.tiles}
        renderItem={renderTiles}
        itemGap={ITEM_GAP}
        shouldRenderMargin={isMoreThanOneSlide}
        name={props.eventTrackingPrefix}
        maxItemsPerSlide={expectedItemsPerSlide}
        className={props.className}
      />
      <HR />
    </>
  );
};

const CircularImageWrapper = styled(AspectRatioPicture)`
  width: ${IMAGE_SIZE}px;
  height: ${IMAGE_SIZE}px;
  border-radius: 50%;
  overflow: hidden;
  margin: 0 auto;
`;

const StyledFiniteScrollCarousel = styled(
  FiniteScrollCarousel.FiniteScrollCarousel as React.ComponentType<
    FiniteScrollCarousel.IProps<CategoryTileAndImageTitleTileType>
  >
)<{
  itemsPerSlide: number;
  maxItemsPerSlide: number;
  shouldRenderMargin: boolean;
}>`
  ${responsiveMediaQueries.tabletAndAbove} {
    // The min-width, width and max-width are set to prevent the case where
    // child container expands, but parent container stay the same.
    // This will cause unexpected scroll while there are enough space to display all children.
    min-width: ${({ itemsPerSlide }) =>
      css`
        ${(DESKTOP_ITEM_WIDTH + ITEM_GAP) * itemsPerSlide}
      `}px;
    width: fit-content;
    max-width: ${({ maxItemsPerSlide }) =>
      css`
        ${(DESKTOP_ITEM_WIDTH + ITEM_GAP) * maxItemsPerSlide}
      `}px;
    margin: ${({ shouldRenderMargin }) =>
      shouldRenderMargin ? "0 35px 0 35px" : 0};
  }

  ${FiniteScrollCarousel.SingleItemWrapper} {
    width: unset;
    padding: unset;
    flex-shrink: unset;
  }

  ${responsiveMediaQueries.mobile} {
    width: calc(100% + ${MARKETPLACE_LEFT_AND_RIGHT_MARGIN_MOBILE * 2}px);
    margin: 0 -${MARKETPLACE_LEFT_AND_RIGHT_MARGIN_MOBILE}px;
  }
`;

// used for hover state underline animation
const AnimatedUnderlineText = styled.span`
  background-image: linear-gradient(
    ${Core.surface.enabled},
    ${Core.surface.enabled}
  );
  background-size: 0 1px;
  background-position: 0 100%;
  background-repeat: no-repeat;
  transition: background-size 1200ms cubic-bezier(0.17, 0.67, 0.24, 1);
`;

const LinkWrapper = styled(Link)`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  min-width: ${DESKTOP_ITEM_WIDTH}px;
  max-width: fit-content;
  margin: 0 ${ITEM_GAP / 2}px;
  position: relative;

  ${responsiveMediaQueries.mobile} {
    min-width: ${MOBILE_ITEM_WIDTH}px;
  }

  ${responsiveMediaQueries.canHover} {
    &:hover {
      ${AnimatedUnderlineText} {
        background-size: 100% 1px;
      }
    }
  }
`;

const InnerLinkWrapper = styled.div`
  align-items: center;
`;

const TextWrapper = styled(Flex).attrs({ align: "center", justify: "center" })`
  position: absolute;
  height: ${IMAGE_SIZE}px;
  width: ${IMAGE_SIZE}px;
  top: 0;
  left: ${getSpacing(12)};

  ${responsiveMediaQueries.tabletAndAbove} {
    left: ${getSpacing("1x")};
  }
`;

const CategoryName = styled(Typography).attrs({
  align: "center",
  maxLines: 2,
  forwardedAs: "p",
})<{
  $isLongCategoryName?: boolean;
}>`
  &&& {
    text-align: center;
    padding-top: 4px;
    text-overflow: ellipsis;
    display: ${(props) =>
      props.$isLongCategoryName ? "-webkit-box" : "inline-block"};
    overflow: hidden;
    // manually set max-height to make sure underline animation is visible
    max-height: 42px;
    width: ${DESKTOP_ITEM_WIDTH}px;
  }
`;

const HR = styled(HorizontalLine)`
  margin: 24px 0 16px;
  ${responsiveMediaQueries.tabletAndAbove} {
    display: none;
  }
`;
